/**
 * Price Variant Translation Utilities
 *
 * Provides hooks and utility functions for translating price variant names.
 * Follows the pluggable feature architecture pattern.
 */

import { useIntl } from '../reactIntl';

/**
 * Hook to get translated price variant name
 *
 * @param {Object} listing - The listing object
 * @param {string} variantName - The original variant name
 * @returns {string} Translated variant name or original if not found
 *
 * @example
 * const translatedName = useTranslatedPriceVariant(listing, variant.name);
 */
export const useTranslatedPriceVariant = (listing, variantName) => {
  const intl = useIntl();
  const currentLocale = (intl.locale || 'sv').split('-')[0];

  return getTranslatedPriceVariant(listing, variantName, currentLocale);
};

/**
 * Get translated price variant name directly (utility function)
 *
 * @param {Object} listing - The listing object
 * @param {string} variantName - The original variant name
 * @param {string} locale - The target locale (e.g., 'en', 'it')
 * @returns {string} Translated variant name or original if not found
 *
 * @example
 * const translatedName = getTranslatedPriceVariant(listing, 'per hour', 'it');
 */
export const getTranslatedPriceVariant = (listing, variantName, locale = 'sv') => {
  if (!listing?.attributes?.publicData?.translations) {
    return variantName;
  }

  const translations = listing.attributes.publicData.translations;
  const variantTranslations = translations[locale]?.priceVariants || [];

  const translatedVariant = variantTranslations.find(
    pv => pv.originalName === variantName
  );

  return translatedVariant?.translatedName || variantName;
};

/**
 * Get all translated price variants for a listing in the current locale
 *
 * @param {Object} listing - The listing object
 * @param {string} locale - The target locale
 * @returns {Array} Array of price variants with translated names
 *
 * @example
 * const variants = getTranslatedPriceVariants(listing, 'it');
 * // Returns: [{ name: 'original', translatedName: 'tradotto', price: 100 }]
 */
export const getTranslatedPriceVariants = (listing, locale = 'sv') => {
  const priceVariants = listing?.attributes?.publicData?.priceVariants || [];

  return priceVariants.map(variant => ({
    ...variant,
    translatedName: getTranslatedPriceVariant(listing, variant.name, locale),
  }));
};
